import { useRef, useSyncExternalStore } from 'react';
import { useReducedMotion } from 'motion/react';
import Button from 'components/global/Button/Primary';
import ProductLockup from 'components/global/ProductLockup';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import { getProductMeta } from 'lib/utils/get-product-meta';
import { useTransparentVideoSrc } from 'hooks/use-transparent-video-src';
import { withFootnotes } from '../footnotes';
import {
  Benefit,
  Body,
  Copy,
  Details,
  Header,
  Heading,
  Item,
  Items,
  Media,
  Section,
  ShopAll,
} from './styles';

export interface ProductLineupItem {
  sku: string;
  benefit: string;
  url: string;
  media: {
    src?: string;
    videoSrc?: string;
    webmVideoSrc?: string;
  };
}

interface ProductLineupProps {
  id?: string;
  heading: string;
  body: string;
  shopAll: { label: string; url: string };
  items: ProductLineupItem[];
}

const subscribe = () => () => {};
const canHover = () => window.matchMedia('(hover: hover)').matches;

const LineupItem = ({ sku, benefit, url, media }: ProductLineupItem) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // The jar rotates on hover, so only hover-capable devices fetch the video
  const hoverCapable = useSyncExternalStore(subscribe, canHover, () => false);
  const videoSrc = useTransparentVideoSrc(media, hoverCapable);
  const reducedMotion = useReducedMotion();

  const play = () => {
    if (!reducedMotion) videoRef.current?.play().catch(() => undefined);
  };

  const reset = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  return (
    <Item onMouseEnter={play} onMouseLeave={reset}>
      <Media>
        <video
          ref={videoRef}
          src={videoSrc}
          poster={media.src}
          aria-hidden
          muted
          playsInline
          loop
        />
      </Media>
      <Details>
        <Copy>
          <ProductLockup
            sku={sku}
            orientation='portrait'
            centered
            size={{ base: 'xxs', desktop: 'xs' }}
          />
          <Benefit>{withFootnotes(benefit)}</Benefit>
        </Copy>
        <Button
          size={{ base: 'sm', desktop: 'md' }}
          href={url}
          // Same label format as control's product cards, so clicks compare across variants
          onClick={() =>
            buttonEventHandler(`${getProductMeta(sku).name}Shop Now`, 'Card')
          }
        >
          Shop Now
        </Button>
      </Details>
    </Item>
  );
};

const ProductLineup = ({
  id,
  heading,
  body,
  shopAll,
  items,
}: ProductLineupProps) => (
  <Section id={id}>
    <Header>
      <Heading>{heading}</Heading>
      <Body>
        {body} <ShopAll href={shopAll.url}>{shopAll.label}</ShopAll>
      </Body>
    </Header>
    <Items>
      {items.map((item) => (
        <LineupItem key={item.sku} {...item} />
      ))}
    </Items>
  </Section>
);

export default ProductLineup;
